import { PlaceholderShimmerBackground, UnstyledButton } from 'utils/fragments';
import styled, { css } from 'styled-components';
import HealthStatTooltip from '../HealthStatTooltip';
import { MeterLabel } from './HealthStatMeter';
import { MetricLabel } from './HealthStats';
import OverflowExpander from './OverflowExpander';
import PricePointMetricIcon from './PricePointMetricIcon';
import Stack from '../Stack';
import VertLine from '../VertLine';
import { tooltips } from '@/copy';

const MINIMIZED_HEIGHT = '2.125rem';

const MetricStatWrapperFragment = css`
    font-size: 0.75rem;
    min-height: ${MINIMIZED_HEIGHT};
    border-radius: 2rem;
    border: 1px solid ${({ theme }) => theme.gray_300};
    padding: 0.25rem 1rem;
    align-items: center;
`;
const MetricStatWrapper = styled(UnstyledButton)`
    ${MetricStatWrapperFragment}
    &:hover {
        background-color: ${({ theme }) => theme.gray_50};
    }
`;
const MetricStatSkeleton = styled.div`
    ${MetricStatWrapperFragment}
    ${PlaceholderShimmerBackground}
    width: 22%;
    background-color: ${({ theme }) => theme.gray_300};
`;

const MetricStatMetricLabel = styled(MetricLabel)`
    font-size: inherit;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.gray_1};
`;
const MetricStatMetric = styled.span`
    font-weight: ${({ theme }) => theme.fontMedium};
    color: ${({ theme }) => theme.gray_1};
    font-family: ${({ theme }) => theme.style_data};
    text-transform: uppercase;
`;
const MetricStatMeterLabel = styled(MeterLabel)`
    font-size: inherit;
    font-weight: ${({ theme }) => theme.fontBold};
`;

const PricePointMetricStatMetric = styled(MetricStatMetric)`
    color: ${({ theme }) => theme.purple_1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

function MetricStat({
    metric,
    metricLabel,
    score,
    scoreLabel,
    scoreLabelShort,
}) {
    return (
        <HealthStatTooltip
            metricLabel={metricLabel}
            label={scoreLabel}
            score={score}
        >
            <MetricStatWrapper>
                <Stack $space=".625rem" $direction="row" $wrap={false}>
                    {tooltips.healthStats[metricLabel].abbrTitleHTML ? (
                        <MetricStatMetricLabel
                            dangerouslySetInnerHTML={{
                                __html: tooltips.healthStats[metricLabel]
                                    .abbrTitleHTML,
                            }}
                        />
                    ) : (
                        <MetricStatMetricLabel>
                            {metricLabel}
                        </MetricStatMetricLabel>
                    )}
                    {metricLabel === 'price point' ? (
                        <PricePointMetricStatMetric>
                            <PricePointMetricIcon
                                score={score}
                                showEmpty={true}
                            />
                        </PricePointMetricStatMetric>
                    ) : (
                        <MetricStatMetric>{metric}</MetricStatMetric>
                    )}
                    <VertLine $height=".875rem" />
                    <MetricStatMeterLabel
                        score={score}
                        $metricLabel={metricLabel}
                    >
                        {scoreLabelShort ||
                            scoreLabel ||
                            tooltips.healthStatsMissingScoreLabel}
                    </MetricStatMeterLabel>
                </Stack>
            </MetricStatWrapper>
        </HealthStatTooltip>
    );
}

function MetricStats({ metricList, healthStats, isLoading }) {
    return (
        <OverflowExpander $minimizedHeight={MINIMIZED_HEIGHT}>
            <Stack
                $space=".5rem"
                $direction="row"
                $wrap={true}
                css="gap: .6875rem .5rem;"
            >
                {metricList.map((metric) => {
                    if (isLoading)
                        return <MetricStatSkeleton key={metric.key} />;
                    return (
                        <MetricStat
                            key={metric.healthStatKey}
                            metric={healthStats?.[metric.healthStatKey]?.metric}
                            metricLabel={metric.healthStatKey}
                            score={healthStats?.[metric.healthStatKey]?.score}
                            scoreLabel={
                                healthStats?.[metric.healthStatKey]?.scoreLabel
                            }
                            scoreLabelShort={
                                healthStats?.[metric.healthStatKey]
                                    ?.scoreLabelShort
                            }
                        />
                    );
                })}
            </Stack>
        </OverflowExpander>
    );
}

export default MetricStats;
